
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../../api/axiosClient";
import { convertPriority, convertPriorityToAPI, decryptedAdminID, formatDisplayDate, getDefaultDateRange } from "../../utility";

const admin = Number(decryptedAdminID());

// Track pending requests globally
const pendingRequests = new Map();
let lastRequestTime = {};
const REQUEST_DEBOUNCE_MS = 500;

// Base filter with admin_ids properly handled
const getInitialFilter = () => ({
  dateRange: { start: '', end: '' },
  autoComplete: '',
  priority: null,
  orderByPriority: false,
  admin_ids: [],
  page: 1,
  limit: 10,
  title: null,
  _adminFilterApplied: false 
});

const initialState = {
  columns: {
    todo: { items: [], total: 0 },
    pending: { items: [], total: 0 },
    completed: { items: [], total: 0 }
  },
  columnFilters: {
    todo: getInitialFilter(),
    pending: getInitialFilter(),
    completed: getInitialFilter()
  },
  globalFilter: {
    dateRange: { start: '', end: '' },
    autoComplete: '',
    priority: null,
    orderByPriority: false,
    admin_ids: [],
    title: null
  },
  loadingStates: {
    todo: false,
    pending: false,
    completed: false
  },
  error: null,
  showFilterModal: null,
  totalAdminCount: 0,
  allAdminIds: [],
  isInitialized: false,
  lastFetchTimestamps: {
    todo: 0,
    pending: 0,
    completed: 0
  }
};

// Helper to generate unique request key
const getRequestKey = (columnKey, params) => {
  return `${columnKey}_${JSON.stringify(params)}`;
};

// Helper to check if request is recent (within last 2 seconds)
const isRequestRecent = (columnKey, params) => {
  const key = getRequestKey(columnKey, params);
  const lastTime = lastRequestTime[key];
  return lastTime && (Date.now() - lastTime) < 2000;
};

// Async thunk to set admin IDs after fetching
export const setAdminIds = createAsyncThunk(
  "followUp/setAdminIds",
  async (adminIds) => {
    return { admin_ids: adminIds, totalCount: adminIds.length, allAdminIds: adminIds };
  }
);

// Modified fetchFollowUps with deduplication
export const fetchFollowUps = createAsyncThunk(
  "followUp/fetchFollowUps",
  async ({ columnKey, status, useColumnFilter = true, page = null, force = false }, { getState, rejectWithValue }) => {
    
    // Generate request params for deduplication
    const requestParams = { columnKey, status, useColumnFilter, page };
    const requestKey = getRequestKey(columnKey, requestParams);
    
    // Check for duplicate in-flight request
    if (!force && pendingRequests.has(requestKey)) {
      console.log(`⏭️ [${columnKey}] Request already in progress, skipping duplicate`);
      return pendingRequests.get(requestKey);
    }
    
    // Check for recent identical request (within 2 seconds)
    if (!force && isRequestRecent(columnKey, requestParams)) {
      console.log(`⏭️ [${columnKey}] Recent identical request detected, skipping`);
      // Return a special value to indicate skip
      return { columnKey, skipped: true };
    }
    
    // Mark request start time
    lastRequestTime[requestKey] = Date.now();
    
    // Create promise for this request
    const requestPromise = (async () => {
      try {
        const state = getState().followUp;
        let filterToUse;
        
        console.log(`🔄 Fetching ${columnKey} - useColumnFilter: ${useColumnFilter}, page: ${page}, force: ${force}`);
        
        if (!useColumnFilter) {
          filterToUse = state.globalFilter;
          console.log(`📌 [${columnKey}] Using GLOBAL filter (forced)`);
        }
        else if (useColumnFilter && state.columnFilters[columnKey]) {
          const columnFilter = state.columnFilters[columnKey];
          const hasActiveFilter = 
            (columnFilter.dateRange?.start && columnFilter.dateRange?.end) ||
            columnFilter.dateRange?.start ||
            columnFilter.dateRange?.end ||
            (columnFilter.autoComplete && columnFilter.autoComplete !== '') ||
            (columnFilter.priority && columnFilter.priority !== '') ||
            columnFilter.orderByPriority === true ||
            (columnFilter.admin_ids && columnFilter.admin_ids.length > 0) ||
            (columnFilter.title && columnFilter.title !== '' && columnFilter.title !== null)||
            columnFilter._adminFilterApplied === true;
          
          if (hasActiveFilter) {
            filterToUse = columnFilter;
            console.log(`📌 [${columnKey}] Using COLUMN filter`);
          } else {
            filterToUse = state.globalFilter;
            console.log(`📌 [${columnKey}] Using GLOBAL filter (no column filter)`);
          }
        } else {
          filterToUse = state.globalFilter;
          console.log(`📌 [${columnKey}] Using GLOBAL filter (default)`);
        }
        
        // Date logic
        let startDate, endDate;
        const columnFilter = state.columnFilters[columnKey];
        const globalFilter = state.globalFilter;
        let activeDateRange = null;

        if (useColumnFilter && columnFilter?.dateRange?.start && columnFilter?.dateRange?.end) {
          activeDateRange = columnFilter.dateRange;
          console.log(`📅 [${columnKey}] Using column filter date range:`, activeDateRange);
        } else if (columnFilter?.dateRange?.start && columnFilter?.dateRange?.end) {
          activeDateRange = columnFilter.dateRange;
          console.log(`📅 [${columnKey}] Using column filter date range (fallback):`, activeDateRange);
        } else if (globalFilter?.dateRange?.start && globalFilter?.dateRange?.end) {
          activeDateRange = globalFilter.dateRange;
          console.log(`📅 [${columnKey}] Using global filter date range:`, activeDateRange);
        } else if (filterToUse.dateRange?.start && filterToUse.dateRange?.end) {
          activeDateRange = filterToUse.dateRange;
          console.log(`📅 [${columnKey}] Using filterToUse date range:`, activeDateRange);
        }

        if (activeDateRange) {
          startDate = activeDateRange.start;
          endDate = activeDateRange.end;
        } else {
          console.log(`📅 [${columnKey}] No date range found, using default for status:`, status);
          const defaultRange = getDefaultDateRange(status);
          startDate = defaultRange.startDate;
          endDate = defaultRange.endDate;
        }

        console.log(`📅 [${columnKey}] Final dates:`, { startDate, endDate });
        
        // const currentPage = page !== null ? page : (filterToUse.page || 1);
        const currentPage = page !== null ? page : (state.columnFilters[columnKey]?.page || filterToUse.page || 1);
        
        const requestBody = {
          startDate: startDate,
          endDate: endDate,
          status: status,
          page: currentPage,
          limit: filterToUse.limit || 10
        };

        // Admin IDs logic
      // Admin IDs logic
let adminIdsToSend = [];

// ✅ Check if "ALL" was explicitly selected (empty array + flag)
if (filterToUse._adminFilterApplied === true && filterToUse.admin_ids && filterToUse.admin_ids.length === 0) {
  // User explicitly selected ALL - send all admin IDs
  adminIdsToSend = state.allAdminIds;
  console.log(`📡 [${columnKey}] ALL explicitly selected, sending all admins:`, adminIdsToSend);
}
else if (filterToUse.admin_ids && filterToUse.admin_ids.length > 0) {
  // Specific admins selected
  adminIdsToSend = filterToUse.admin_ids;
  console.log(`📡 [${columnKey}] Using admin_ids from filter:`, adminIdsToSend);
} 
else if (state.allAdminIds && state.allAdminIds.length > 0) {
  adminIdsToSend = state.allAdminIds;
  console.log(`📡 [${columnKey}] Using allAdminIds:`, adminIdsToSend);
}
else if (state.globalFilter.admin_ids && state.globalFilter.admin_ids.length > 0) {
  adminIdsToSend = state.globalFilter.admin_ids;
  console.log(`📡 [${columnKey}] Using globalFilter.admin_ids:`, adminIdsToSend);
}
else {
  adminIdsToSend = admin ? [admin] : [];
  console.log(`📡 [${columnKey}] Using fallback admin_ids:`, adminIdsToSend);
}

if (adminIdsToSend.length > 0) {
  requestBody.adminIds = adminIdsToSend;
}
        
        if (filterToUse.priority && filterToUse.priority !== '' && filterToUse.priority !== null) {
          requestBody.priority = convertPriorityToAPI(filterToUse.priority);
        }
        
        if (filterToUse.orderByPriority === true) {
          requestBody.orderByPriority = true;
        }

        if (filterToUse.title && filterToUse.title !== '' && filterToUse.title !== null) {
          requestBody.title = filterToUse.title;
          console.log(`📡 [${columnKey}] Sending title filter:`, filterToUse.title);
        }
        
        console.log(`📡 [${columnKey}] Final Request Body:`, requestBody);
        
        const response = await axiosClient.post("/getFollowUpsFilter", requestBody);
        const result = response.data;
        
        let items = [];
        let total = 0;
        
        if (result.success && result.data) {
          items = result.data.data || result.data;
          total = result.data.total || 0;
        } else if (result.data && !result.success) {
          items = result.data.data || [];
          total = result.data.total || 0;
        } else if (Array.isArray(result)) {
          items = result;
          total = result.length;
        } else if (result.data && Array.isArray(result.data)) {
          items = result.data;
          total = result.data.length;
        }
        
        if (filterToUse.autoComplete && filterToUse.autoComplete.trim() !== "") {
          const searchTerm = filterToUse.autoComplete.toLowerCase();
          items = items.filter(item => 
            (item.title?.toLowerCase().includes(searchTerm) ||
            item.description?.toLowerCase().includes(searchTerm))
          );
          total = items.length;
        }
        
        let transformedTasks = items.map(item => ({
          id: item.followup_id || item.id,
          title: item.title || '',
          description: item.description || '',
          priority: convertPriority(item.priority),
          date: item.followup_date ? item.followup_date.split(' ')[0] : '',
          displayDate: formatDisplayDate(item.followup_date),
          user: item.admin_name || null,
          adminName: item.admin_name,
          candidateName: item.candidate_first_name || (item.candidate_lead_first_name||"NO NAME"),
          candidateId: item.entity_type=== "LEAD" ? item.candidate_lead_id : item.candidate_id,
          entity_type: item.entity_type,
          entity_id: item.entity_id,
          followup_type: item.followup_type,
          status: item.status
        }));
        
        if (filterToUse.orderByPriority === true) {
          const priorityOrder = { critical: 1, high: 2, medium: 3, low: 4 };
          transformedTasks.sort((a, b) => {
            return (priorityOrder[a.priority] || 5) - (priorityOrder[b.priority] || 5);
          });
        }
        
        return { columnKey, items: transformedTasks, total, page: currentPage, skipped: false };
      } catch (err) {
        console.error(`❌ Error fetching ${columnKey}:`, err);
        return rejectWithValue(err.response?.data || "Failed to fetch");
      } finally {
        // Remove from pending requests after completion
        pendingRequests.delete(requestKey);
      }
    })();
    
    // Store pending request
    pendingRequests.set(requestKey, requestPromise);
    
    return requestPromise;
  }
);

// Add debounced version for filter changes
let debounceTimeout;
export const fetchFollowUpsDebounced = (params) => {
  return (dispatch) => {
    clearTimeout(debounceTimeout);
    return new Promise((resolve) => {
      debounceTimeout = setTimeout(() => {
        resolve(dispatch(fetchFollowUps({ ...params, force: false })));
      }, REQUEST_DEBOUNCE_MS);
    });
  };
};

// Modified fetchAllColumns with deduplication
let fetchAllColumnsPromise = null;

export const fetchAllColumns = createAsyncThunk(
  "followUp/fetchAllColumns",
  async (_, { getState, dispatch }) => {
    // Return existing promise if one is in progress
    if (fetchAllColumnsPromise) {
      console.log('⏭️ fetchAllColumns already in progress, returning existing promise');
      return fetchAllColumnsPromise;
    }
    
    fetchAllColumnsPromise = (async () => {
      try {
        const state = getState().followUp;
        const globalFilter = state.globalFilter;
        
        const hasGlobalFilter = 
          (globalFilter.dateRange?.start && globalFilter.dateRange?.end) ||
          globalFilter.dateRange?.start ||
          globalFilter.dateRange?.end ||
          (globalFilter.autoComplete && globalFilter.autoComplete !== '') ||
          (globalFilter.priority && globalFilter.priority !== '') ||
          globalFilter.orderByPriority === true ||
          (globalFilter.admin_ids && globalFilter.admin_ids.length > 0) ||
          (globalFilter.title && globalFilter.title !== '' && globalFilter.title !== null)||
           globalFilter._adminFilterApplied === true; // ✅ ADD THIS
        
        console.log('🔄 Fetching all columns - Has Global Filter:', hasGlobalFilter, globalFilter);
        
        // Use Promise.allSettled to prevent one failure from stopping others
        const results = await Promise.allSettled([
          dispatch(fetchFollowUps({ columnKey: 'todo', status: 'TODO', useColumnFilter: !hasGlobalFilter, force: true })),
          dispatch(fetchFollowUps({ columnKey: 'pending', status: 'PENDING', useColumnFilter: !hasGlobalFilter, force: true })),
          dispatch(fetchFollowUps({ columnKey: 'completed', status: 'COMPLETED', useColumnFilter: !hasGlobalFilter, force: true }))
        ]);
        
        // Log any failures
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const columns = ['todo', 'pending', 'completed'];
            console.error(`❌ Failed to fetch ${columns[index]}:`, result.reason);
          }
        });
        
        return results;
      } finally {
        // Clear the promise after completion
        setTimeout(() => {
          fetchAllColumnsPromise = null;
        }, 100);
      }
    })();
    
    return fetchAllColumnsPromise;
  }
);

// Add initialization thunk
export const initializeFollowUps = createAsyncThunk(
  "followUp/initialize",
  async (_, { getState, dispatch }) => {
    const state = getState().followUp;
    
    if (state.isInitialized) {
      console.log('✅ Already initialized, skipping...');
      return;
    }
    
    console.log('🚀 Initializing follow-ups...');
    await dispatch(fetchAllColumns());
    return { initialized: true };
  }
);

const followUpSlice = createSlice({
  name: "followUp",
  initialState,
  reducers: {
  updateColumnFilter: (state, action) => {
  const { columnKey, filterData } = action.payload;
  
  let cleanFilterData = { ...filterData };
  
  if (cleanFilterData.admin_ids === undefined && state.columnFilters[columnKey]) {
    cleanFilterData.admin_ids = state.columnFilters[columnKey].admin_ids;
  }
  
  Object.keys(cleanFilterData).forEach(key => {
    const value = cleanFilterData[key];
    
    if (key === 'dateRange') {
      if (!value?.start && !value?.end) {
        cleanFilterData[key] = { start: '', end: '' };
      }
      return;
    }
    
    // ✅ Don't delete _adminFilterApplied even if false
    if (key === '_adminFilterApplied') {
      return;
    }
    
    if (value === undefined) {
      delete cleanFilterData[key];
    }
  });
  
  state.columnFilters[columnKey] = {
    ...state.columnFilters[columnKey],
    ...cleanFilterData,
    page: 1
  };
},

   updateGlobalFilter: (state, action) => {
  let filterData = { ...action.payload };
  
  if (filterData.admin_ids === undefined && state.globalFilter) {
    filterData.admin_ids = state.globalFilter.admin_ids;
  }
  
  Object.keys(filterData).forEach(key => {
    const value = filterData[key];
    
    if (key === 'dateRange') {
      if (!value?.start && !value?.end) {
        filterData[key] = { start: '', end: '' };
      }
      return;
    }
    
    // ✅ Don't delete _adminFilterApplied even if false
    if (key === '_adminFilterApplied') {
      return;
    }
    
    if (value === undefined) {
      delete filterData[key];
    }
  });
  
  state.globalFilter = { ...state.globalFilter, ...filterData };
},
    
    resetColumnFilter: (state, action) => {
      const { columnKey } = action.payload;
      state.columnFilters[columnKey] = {
        dateRange: { start: '', end: '' },
        autoComplete: '',
        priority: null,
        orderByPriority: false,
        admin_ids: [],
        page: 1,
        limit: 10,
        title: null
      };
      console.log(`Reset column filter for ${columnKey}`);
    },

    resetGlobalFilter: (state) => {
      state.globalFilter = {
        dateRange: { start: '', end: '' },
        autoComplete: '',
        priority: null,
        orderByPriority: false,
        admin_ids: [],
        title: null 
      };
      console.log('Reset global filter');
    },
    
    setColumnPage: (state, action) => {
      const { columnKey, page } = action.payload;
      if (state.columnFilters[columnKey]) {
        state.columnFilters[columnKey] = {
          ...state.columnFilters[columnKey],
          page: page
        };
        console.log(`Updated page for ${columnKey} to ${page}, preserving filter:`, state.columnFilters[columnKey]);
      }
    },
    
    setShowFilterModal: (state, action) => {
      state.showFilterModal = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    setInitialized: (state, action) => {
      state.isInitialized = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setAdminIds.fulfilled, (state, action) => {
        const { admin_ids, totalCount, allAdminIds } = action.payload;
        state.totalAdminCount = totalCount;
        state.allAdminIds = allAdminIds || admin_ids;
        
        state.globalFilter.admin_ids = [...admin_ids];
        state.columnFilters.todo.admin_ids = [...admin_ids];
        state.columnFilters.pending.admin_ids = [...admin_ids];
        state.columnFilters.completed.admin_ids = [...admin_ids];
        
        console.log('✅ Admin IDs set in all filters');
      })
      .addCase(initializeFollowUps.fulfilled, (state, action) => {
        state.isInitialized = true;
        console.log('✅ Follow-ups initialized successfully');
      })
      .addCase(fetchFollowUps.pending, (state, action) => {
        const { columnKey } = action.meta.arg;
        state.loadingStates[columnKey] = true;
        state.error = null;
      })
      .addCase(fetchFollowUps.fulfilled, (state, action) => {
        // ✅ Add safety check for action.payload
        if (!action.payload) {
          console.warn('fetchFollowUps.fulfilled called with undefined payload');
          return;
        }
        
        const { columnKey, items, total, page, skipped } = action.payload;
        
        // Skip if this was a skipped request
        if (skipped) {
          console.log(`⏭️ [${columnKey}] Skipped request, not updating state`);
          state.loadingStates[columnKey] = false;
          return;
        }
        
        // Ensure columnKey exists
        if (!columnKey || !state.columns[columnKey]) {
          console.error(`Invalid columnKey: ${columnKey}`, action.payload);
          return;
        }
        
        state.columns[columnKey].items = items || [];
        state.columns[columnKey].total = total || 0;
        
        if (state.columnFilters[columnKey]) {
          state.columnFilters[columnKey].page = page || 1;
        }
        
        state.loadingStates[columnKey] = false;
        state.lastFetchTimestamps[columnKey] = Date.now();
        
        console.log(`✅ ${columnKey} loaded: ${items?.length || 0} items, total: ${total || 0}`);
      })
      .addCase(fetchFollowUps.rejected, (state, action) => {
        const { columnKey } = action.meta.arg;
        state.loadingStates[columnKey] = false;
        state.error = action.payload || "Failed to fetch data";
        console.error(`❌ ${columnKey} failed:`, state.error);
      });
  }
});

export const {
  updateColumnFilter,
  resetColumnFilter,
  updateGlobalFilter,
  resetGlobalFilter,
  setColumnPage,
  setShowFilterModal,
  clearError,
  setInitialized
} = followUpSlice.actions;

export default followUpSlice.reducer;